import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { ModalHeader } from '@ui/components/Modal'
import type { VerifyStrategyType } from '@ca/common/types'
import { trpc } from 'api'
import FormattedText from '@ui/typography/FormattedText'
import Button from '@ui/buttons/Button'
import Email from './Strategies/EmailAuthContent'
import Mobile from './Strategies/MobileAuthContent'
import Staff from './Strategies/StaffAuthContent'
import { VerifyContentProps } from './Strategies/types'
import { useToast } from '@ui/toasts/useToastManager'

export const AuthVerifyContent = ({
  verifyRes,
  customer,
  replace = true,
  onSuccess,
}: VerifyContentProps) => {
  const { t } = useTranslation(['auth', 'common', 'toasts'])
  const toast = useToast()
  const verifyRequest = trpc.customer.verifyRequest.useMutation()
  const response = verifyRequest.data || verifyRes
  const strategy: VerifyStrategyType = response?.strategy || 'email'
  const altStrategy = response?.availableStrategies?.[0] // For now we only display 1st available alt strategy

  const switchStrategy = (login: string, nextStrategy: VerifyStrategyType) => {
    verifyRequest.mutate(
      {
        login,
        strategy: nextStrategy,
      },
      {
        onError: () => {
          toast({
            variant: 'error',
            title: t('toasts:error.default.title'),
            text: t('toasts:error.default.desc'),
          })
        },
      }
    )
  }

  const props: VerifyContentProps = {
    verifyRes: response,
    customer,
    replace,
    onSuccess,
  }

  return (
    <View className="w-full gap-16">
      <ModalHeader ns="auth" path={`verify.${strategy}`} />
      {strategy === 'email' && <Email {...props} />}
      {strategy === 'phone' && <Mobile {...props} />}
      {strategy === 'staff' && <Staff {...props} />}
      {altStrategy && (
        <View className="items-center gap-6">
          <FormattedText
            variant="MD"
            text={t(`auth:verify.${strategy}.alt.text`)}
            className="color-black/50 text-center"
            values={{
              maskedLogin: altStrategy.maskedLogin,
            }}
          />
          <Button
            variant="outlined"
            label={t(`auth:verify.${strategy}.alt.cta`)}
            loading={verifyRequest.isPending}
            onPress={() => {
              switchStrategy(response.login, altStrategy.strategy)
            }}
          />
        </View>
      )}
    </View>
  )
}
